import { useLookupsStore } from '@repo/store/useLookupsStore'

export const validateByFieldType = ({ fieldType, value }) => {
  const regularExpressions = {
    Date: /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/,
    Decimal: /\d{1,7}(\.\d{1,2})?/,
    Integer: /\d{1,7}/,
    Money: /\d{1,7}(\.\d{1,2})?/,
    Email: /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/
  }

  if (!regularExpressions[fieldType]) return true

  return regularExpressions[fieldType].test(value)
}

export const categorizeFieldType = ({ fieldType }) => {
  const fieldTypeIsString =
    fieldType === 'Address' ||
    fieldType === 'Email' ||
    fieldType === 'Multiline' ||
    fieldType === 'Text' ||
    fieldType === 'URL'

  const fieldTypeIsNumber = fieldType === 'Decimal' || fieldType === 'Integer' || fieldType === 'Money'

  const fieldTypeIsAnId = fieldType === 'Contact' || fieldType === 'Org' || fieldType === 'List'

  return { fieldTypeIsString, fieldTypeIsNumber, fieldTypeIsAnId }
}

export const validateCustomDataField = async ({ field, value, lookupsStoreMock }) => {
  const { fieldType, length, maxValue, minValue, regularExpression, required, expertTypeId, expertiseAreaId } =
    field || {}
  const validationErrors = []

  // if the field is not required and no value is provided, return early as this is valid and no further validation is needed
  const valueIsSet = value !== null && value !== undefined && value !== '' // 0 and false count as values
  if (!required && !valueIsSet) return validationErrors

  // if the field is required and no value is provided, add an error and return early
  if (required && !value) {
    validationErrors.push('A value is required for this field')
    return validationErrors
  }

  // from here on we can assume that a value is provided

  // validate the field type based on preset regular expressions for each field type
  const fieldTypeValid = validateByFieldType({ fieldType, value })
  if (!fieldTypeValid) validationErrors.push(`The supplied value is not a valid ${fieldType}`)

  const { fieldTypeIsString, fieldTypeIsNumber, fieldTypeIsAnId } = categorizeFieldType({ fieldType })
  const typeOfValue = typeof value

  // validate strings
  if (fieldTypeIsString) {
    if (typeOfValue !== 'string') validationErrors.push('The supplied value must be a string')

    // validate the length
    if (length && value.length > length)
      validationErrors.push(`The supplied value has too many characters (limit: ${length})`)
  }

  // validate numbers
  if (fieldTypeIsNumber) {
    if ((typeOfValue !== 'number' && typeOfValue !== 'string') || Number.isNaN(Number(value)))
      validationErrors.push('The supplied value should be a valid number')

    // validate the length
    const lengthWithoutCharacters = value ? String(value).replace(/[^0-9]/g, '').length : 0
    if (length && lengthWithoutCharacters > length)
      validationErrors.push(`The supplied value has too many digits (limit: ${length})`)

    if (maxValue && Number(value) > Number(maxValue))
      validationErrors.push(`The supplied value is greater than the allowed maximum value (${maxValue})`)

    if (minValue && Number(value) < Number(minValue))
      validationErrors.push(`The supplied value less than the allowed minimum value (${minValue})`)
  }

  // validate ids
  if (fieldTypeIsAnId && typeOfValue !== 'string' && typeOfValue !== 'number')
    validationErrors.push('The supplied value is not a valid id')

  // validate contacts
  if (fieldType === 'Contact') {
    const { fetchContactById, expertTypesById, expertAreasById } = lookupsStoreMock || useLookupsStore.getState()
    let contact
    try {
      contact = await fetchContactById({ id: value })
    } catch (err) {
      validationErrors.push('The supplied value is not a valid contact id')
    }

    if (contact && expertTypeId) {
      const { description } = expertTypesById[expertTypeId] || { description: '' }
      const contactExpertTypeId = contact?.expertTypeId || null
      if (!contactExpertTypeId || contactExpertTypeId !== expertTypeId) {
        validationErrors.push(`The supplied Contact is not the correct Expert Type (${description})`)
      }
    }

    if (contact && expertiseAreaId) {
      const { description } = expertAreasById[expertiseAreaId] || { description: '' }
      const contactExpertiseAreaId = contact?.expertiseAreaId || null
      if (!contactExpertiseAreaId || contactExpertiseAreaId !== expertiseAreaId) {
        validationErrors.push(`The supplied Contact is not the correct Expertise Area (${description})`)
      }
    }
  }

  // validate organisations
  if (fieldType === 'Org') {
    const { fetchOrganisationById, expertTypesById } = lookupsStoreMock || useLookupsStore.getState()
    let organisation
    try {
      organisation = await fetchOrganisationById({ id: value })
    } catch (err) {
      validationErrors.push('The supplied value is not a valid organisation id')
    }

    if (expertTypeId) {
      const { description } = expertTypesById[expertTypeId] || { description: '' }
      const organisationExpertTypeId = organisation?.expertTypeId || null
      if (!organisationExpertTypeId || organisationExpertTypeId !== expertTypeId) {
        validationErrors.push(`The supplied Organisation is not the correct Expert Type (${description})`)
      }
    }
  }

  // validate the regular expression
  if (regularExpression && !regularExpression.test(value))
    validationErrors.push('The supplied value does not meet the regular expression rules')

  return validationErrors
}

export const validateMultipleCustomDataFields = async ({ fields, values }) => {
  console.log('==================== VALIDATING FORM')

  const fieldsToValidate = Object.entries(values)
  let allValidationErrors = {}
  let errorCount = 0

  for (let i = 0; i < fieldsToValidate.length; i++) {
    const [key, value] = fieldsToValidate[i]
    const field = fields.find((f) => f.fieldId === key || f.customDataFieldId === key)
    const fieldValidationErrors = await validateCustomDataField({ field, value })

    if (fieldValidationErrors?.length) {
      errorCount += fieldValidationErrors.length
      allValidationErrors = { ...allValidationErrors, [key]: fieldValidationErrors }
    }
  }

  return { allValidationErrors, errorCount }
}
